import { useEffect, useState } from "react";
import { useAsync } from "../useAsync.js";
import { api, type AlertRule } from "../api.js";
import { SkeletonScreen } from "../Skeleton.js";
import { DEFAULT_ALERT_RULES } from "@addressium/domain";

const METRIC_LABEL: Record<string, string> = {
  complaint_rate: "Complaint rate",
  bounce_rate: "Bounce rate",
  send_failures: "Send failures (count)",
  reputation: "Reputation",
};

/**
 * Deliverability thresholds (#217) — the rates at which admins are alerted.
 * Alerts go to the org's SNS topic and never stop a send (#366); without a
 * topic nobody hears about a breach, so that is flagged as loudly as having no
 * thresholds.
 *
 * A missing config reads back as `null`, and that is rendered as UNPROTECTED
 * rather than as zeroed thresholds: zeros look like a deliberate setting, and an
 * org that silently has no alerting is exactly what this issue was about.
 *
 * The form under that banner starts from the provisioning defaults (#349). It
 * used to start from `[]`: the banner said "set thresholds below" over a table
 * with no rows, and Save wrote `{ rules: [] }`, which made the banner go away
 * and left the org exactly as unprotected. The defaults are shown as
 * suggestions — the banner stays until a save actually lands. A stored empty
 * rule set is handled the same way as a missing one.
 */
export function Deliverability({ org }: { org: string }) {
  const loaded = useAsync(() => api.alertConfig(org), [org]);
  const [rules, setRules] = useState<AlertRule[] | null>(null);
  const [topic, setTopic] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  // Whether THIS screen has saved a config since it loaded — the read is not
  // re-run, so `loaded.data` stays null after the first save of an empty org.
  const [saved, setSaved] = useState(false);

  // A stored `{ rules: [] }` is the state the old empty form wrote (#349), and
  // it protects nothing — so it is treated like no record at all: defaults
  // seeded, banner shown. Reading it as "configured" left those orgs with a
  // clean-looking page, no rows and a disabled Save: no way back.
  const storedRules = loaded.data?.rules.length ? loaded.data.rules : null;

  useEffect(() => {
    setSaved(false);
    if (loaded.data) setTopic(loaded.data.snsTopicArn ?? "");
    if (storedRules) {
      setRules(storedRules);
    } else if (!loaded.loading && !loaded.error) {
      setRules(DEFAULT_ALERT_RULES.map((r) => ({ ...r })));
    }
  }, [loaded.data, loaded.loading, loaded.error]);

  const unprotected = !storedRules && !saved;

  const update = (i: number, patch: Partial<AlertRule>) =>
    setRules((rs) => (rs ? rs.map((r, j) => (j === i ? { ...r, ...patch } : r)) : rs));

  const save = async () => {
    if (!rules) return;
    setSaving(true);
    setMsg("");
    try {
      // haltAt below warnAt is rejected server-side; catch it here too so the
      // operator is told which row is wrong rather than getting one message.
      const bad = rules.findIndex((r) => r.haltAt < r.warnAt);
      if (bad >= 0) throw new Error(`${METRIC_LABEL[rules[bad]!.metric]}: critical must not be below warning`);
      await api.saveAlertConfig({ orgId: org, snsTopicArn: topic.trim() || undefined, rules, notifyTargets: [] });
      setSaved(true);
      setMsg("Saved.");
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loaded.loading) return <SkeletonScreen />;
  if (loaded.error) return <div className="error">{loaded.error}</div>;

  return (
    <div>
      <h2>Deliverability</h2>
      <p className="muted">
        When a campaign crosses a threshold, a warning or critical alert goes to the alert
        topic. Alerts never stop a send: you decide whether to. Rates are fractions of
        messages sent — 0.005 is 0.5%.
      </p>

      {unprotected && (
        <div className="card" style={{ borderColor: "#b45309" }}>
          <strong>This organization has no thresholds.</strong>
          <div className="muted">
            Nobody will be told when a campaign starts generating complaints. The rows below are
            suggested defaults and are not saved yet — review them and press Save thresholds.
          </div>
        </div>
      )}

      <div className="card">
        <label>
          Alert topic ARN
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="arn:aws:sns:…" />
        </label>
        {/* The stored topic, not the field: a typed-but-unsaved ARN still reaches nobody. */}
        {!(saved ? topic.trim() : loaded.data?.snsTopicArn) ? (
          <div className="err">
            No alert topic is set, so a breach reaches nobody. Create an SNS topic in AWS,
            subscribe your email or Slack to it, and paste its ARN here.
          </div>
        ) : (
          <div className="muted">
            Admins subscribe to this SNS topic in AWS (email, Slack or anything SNS delivers to).
          </div>
        )}
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Warning at</th>
            <th>Critical at</th>
            <th>Enabled</th>
          </tr>
        </thead>
        <tbody>
          {(rules ?? []).map((r, i) => (
            <tr key={r.metric}>
              <td>
                {METRIC_LABEL[r.metric] ?? r.metric}
                {r.metric === "reputation" && (
                  <div className="muted">No live signal yet — leave disabled.</div>
                )}
              </td>
              <td>
                <input
                  type="number"
                  step="0.001"
                  value={r.warnAt}
                  onChange={(e) => update(i, { warnAt: Number(e.target.value) })}
                />
                {/* Entered as a fraction; 0.005 and 0.05 are one keystroke apart and 10x apart in effect. */}
                <span className="muted" style={{ marginLeft: 6 }}>{asPercent(r.warnAt)}</span>
              </td>
              <td>
                <input
                  type="number"
                  step="0.001"
                  value={r.haltAt}
                  onChange={(e) => update(i, { haltAt: Number(e.target.value) })}
                />
                <span className="muted" style={{ marginLeft: 6 }}>{asPercent(r.haltAt)}</span>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={r.enabled}
                  onChange={(e) => update(i, { enabled: e.target.checked })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* `!rules?.length`, not `!rules`: `[]` is truthy, and saving an empty
          rule set is how an org ended up with no alerts at all (#349).
          The server refuses it too. */}
      <button className="btn" onClick={save} disabled={saving || !rules?.length}>
        {saving ? "Saving…" : "Save thresholds"}
      </button>
      {msg && <span style={{ marginLeft: 12 }}>{msg}</span>}
    </div>
  );
}

/** A threshold fraction as the percentage it means, e.g. 0.005 -> "= 0.5%". */
function asPercent(fraction: number): string {
  return Number.isFinite(fraction) ? `= ${+(fraction * 100).toFixed(3)}%` : "";
}
